import { GetFrequentlySearchedAreasResponse } from '@engelvoelkersamericas/api';
import { extractDomain, extractSubdomain } from '@util/getURL';
import { formatPhoneNumber } from '@util/phoneNumber';
import { isValidHttpUrl } from '@util/validateUrl';
import { apiClient } from 'api-client';
import posthog from 'posthog-js';

// Format address for potential bad data, replace all ", , " and " , , " in addresses fields like "284-286 1st Street, , Jersey City, NJ 07302" to ", Jersey City, NJ 07302"
export const checkAndFixPotentialAddresses = (address: string) => {
  if (address) {
    return address.replace(/ , , /g, ', ').replace(/, , /g, ', ');
  }
  return '';
};

// Queries list of properties
export const generateResults = async ({
  sortType,
  offset,
  limit,
  api,
  name,
  shops,
  searchInputOverride,
  enhancementFilter,
}: {
  sortType: { sortKey: string; sortDir: string };
  enhancementFilter?: object;
  offset: number;
  limit: number;
  api: any;
  name?: string;
  shops?: string;
  searchInputOverride?: boolean;
}) => {
  let params: any = {
    sortKey: sortType.sortKey,
    sortDir: sortType.sortDir,
    enhancementFilter,
    offset,
    limit,
  };
  if (name) {
    name = encodeURIComponent(name);
    params = { ...params, [searchInputOverride ? 'search' : 'name']: name };
  }
  if (shops) {
    params = { ...params, shops };
  }
  let search = await api.searchShops(params);
  const data = await search;
  return data;
};

export const replaceSpecialChars = (str) => {
  return str
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[ç]/g, 'c')
    .replace(/[ñ]/g, 'n')
    .replace(/['"]/g, '');
};

export const makeLocationNameUrlSafe = (locationName) => {
  return replaceSpecialChars(locationName.replace(/ /g, '-')).toLowerCase();
};

export const mapResults = (results: any[]) => {
  return results && results.length > 0
    ? results.map((r) => {
        const belongsToShopGroup = !!r.shopGroup?.id;

        const subdomain = belongsToShopGroup
          ? r.shopGroup?.domain?.split('.')?.[0]
          : r.contact?.website?.split('.')?.[0];

        const currentHost = window.location.host;
        const currentHostSubDomain = extractSubdomain();
        const baseUrl = subdomain
          ? currentHost.includes('vercel.app')
            ? `${window.location.protocol}//${extractDomain(currentHost)}/shops/${subdomain}`
            : `${window.location.protocol}//${subdomain}.${extractDomain(
                currentHost,
                currentHostSubDomain,
              )}`
          : '';
        const urlSafeSubShopName = r.locationName ? makeLocationNameUrlSafe(r.locationName) : '';
        const contactUsUrl = `${baseUrl}/our-shops/${urlSafeSubShopName}`;
        const advisorsUrl =
          r.associatedAdvisors.length > 0
            ? belongsToShopGroup
              ? `${contactUsUrl}/our-advisors`
              : `${baseUrl}/our-advisors`
            : null;

        const mappedData = {
          image: r.shopImage,
          title: r.locationName || r.name,
          address: checkAndFixPotentialAddresses(r.contact?.address),
          phone: formatPhoneNumber(r.contact?.phone),
          breNumber: r.licenses && r.licenses.length > 0 ? r.licenses[0].license : '',
          urlWebsite: baseUrl ? baseUrl : '',
          urlAdvisors: advisorsUrl,
          urlContactUs: contactUsUrl,
          urlFacebook: getSocialUrl(r.socialProfiles, 'facebook'),
          urlTwitter: getSocialUrl(r.socialProfiles, 'twitter'),
          urlLinkedin: getSocialUrl(r.socialProfiles, 'linkedin'),
          urlInstagram: getSocialUrl(r.socialProfiles, 'instagram'),
          urlYoutube: getSocialUrl(r.socialProfiles, 'youtube'),
          urlTiktok: getSocialUrl(r.socialProfiles, 'tiktok'),
          isShopGroupHomePage: belongsToShopGroup,
        };
        return mappedData;
      })
    : [];
};

export const mapShopFooter = (pageType, shopData) => {
  // Address info comes from contact field for shops
  // and directly from shopData for shop groups
  const shopFooter = {
    address: shopData?.contact?.address || shopData?.address || '',
    address2: shopData?.contact?.address2 || shopData?.address2 || '',
    street1: shopData?.contact?.street1 || '',
    street2: shopData?.contact?.street2 || '',
    city: shopData?.contact?.city || shopData?.city || '',
    country: shopData?.contact?.country || shopData?.country || '',
    phone: shopData?.contact?.phone || shopData?.phone || '',
    postalCode: shopData?.contact?.postalCode || shopData?.postalCode || '',
    name: shopData?.legalName ? shopData?.legalName : shopData?.name || '',
    licenses: shopData?.licenses,
    state: shopData?.contact?.state || shopData?.state || '',
    urlInstagram: getSocialUrl(shopData?.socialProfiles, 'instagram'),
    urlTwitter: getSocialUrl(shopData?.socialProfiles, 'twitter'),
    urlFacebook: getSocialUrl(shopData?.socialProfiles, 'facebook'),
    urlLinkedin: getSocialUrl(shopData?.socialProfiles, 'linkedin'),
    urlYoutube: getSocialUrl(shopData?.socialProfiles, 'youtube'),
    urlTiktok: getSocialUrl(shopData?.socialProfiles, 'tiktok'),
  };
  return shopFooter;
};

export const getFrequentlySearchedAreas = async (
  shopOrShopGroupId?: string,
): Promise<GetFrequentlySearchedAreasResponse | null> => {
  try {
    const fsaParams = {
      shopOrShopGroupId: shopOrShopGroupId ?? 'americas',
    };

    const res = await apiClient.getFrequentlySearchedAreas(
      {
        getFrequentlySearchedAreaDto: fsaParams,
      },
      {
        timeout: 5000,
      },
    );
    return res.data;
  } catch (err) {
    posthog.captureException(err);
    return null;
  }
};

export const getSocialUrl = (data, socialPlatform) => {
  let url = '';
  if (data && data.length > 0) {
    //assign url to the first social profile that matches the social platform
    data.some((profile) => {
      if (profile.type === socialPlatform) {
        url = profile.url;
        return true;
      }
    });
  }
  return url;
};

const getUrl = (url: string, shopname: string, postfix: string) => {
  const subdomain = isValidHttpUrl(url);
  if (subdomain != '') {
    return `${subdomain}/${postfix}`;
  }
  return `/shops/${shopname}/${postfix}`;
};
